import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getContextAwareToken } from "../utils/storageHelper";

export const ProfileLayout = () => {
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNo: "",
    homeNumber: "",
    workPhone: "",
    dob: "",
    gender: "",
    age: "",
    messageOption: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    occupation: "",
    maritalStatus: "",
    reason: "",
    emergencyFirst: "",
    emergencyLast: "",
    emergencyContact: "",
    emergencyRelation: "",
    healthcareProvider: "",
  });
  const { user }: any = useAuth();
  console.log("Authenticated User:", user);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    console.log("Profile Data:", profile);
    alert("Profile saved successfully!");
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!user?.email) return;
      const token = getContextAwareToken(user?.token, user?.role);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/users?email=${user.email}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const resp = await response.json();
        if (response.ok) {
          setProfile((prev) => ({
            ...prev,
            firstName: resp.data.first_name || "",
            lastName: resp.data.last_name || "",
            email: resp.data.email || "",
            contactNo: resp.data.contact_number || "",
            dob: resp.data.birth_date || "",
            gender: resp.data.gender === "MALE" ? "Male" : "Female",
          }));
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchUserDetails();
  }, [user?.email, user?.token, user?.role]);

  return (
    <Box
      sx={{
        backgroundColor: "#F9FAFB",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        py: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "90%",
          maxWidth: 1200,
          backgroundColor: "#fff",
          borderRadius: 3,
          p: 4,
          boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          My Profile
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={4}
          sx={{
            mb: 4,
            "& .active": { color: "#007BFF", fontWeight: 600 },
          }}
        >
          <Typography className="active">1. Patient Information</Typography>
          <Typography color="text.secondary">
            2. Family Information
          </Typography>
          <Typography color="text.secondary">
            3. Insurance Information
          </Typography>
        </Stack>

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 2, mt: 1 }}
        >
          Basic Information
        </Typography>

        <Stack spacing={2} sx={{ mb: 4 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              required
              label="First Name"
              name="firstName"
              value={profile.firstName}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              required
              label="Last Name"
              name="lastName"
              value={profile.lastName}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              required
              label="Email"
              name="email"
              value={profile.email}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              required
              label="Contact No"
              name="contactNo"
              value={profile.contactNo}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              label="Home Number"
              name="homeNumber"
              value={profile.homeNumber}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="Work Phone"
              name="workPhone"
              value={profile.workPhone}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="Date of Birth"
              name="dob"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={profile.dob}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <FormControl sx={{ flex: 1, minWidth: 250 }}>
              <Typography sx={{ fontSize: 13, mb: 0.5, color: "#555" }}>
                Gender
              </Typography>
              <RadioGroup
                row
                name="gender"
                value={profile.gender}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="Male"
                  control={<Radio size="small" />}
                  label="Male"
                />
                <FormControlLabel
                  value="Female"
                  control={<Radio size="small" />}
                  label="Female"
                />
              </RadioGroup>
            </FormControl>
          </Stack>

          {/* Row 3 */}
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              label="Age"
              name="age"
              value={profile.age}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="Can we leave a message?"
              name="messageOption"
              placeholder="Home / Cell / Work"
              value={profile.messageOption}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
          </Stack>

          {/* Row 4 */}
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              label="Address 1"
              name="address1"
              value={profile.address1}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Address 2"
              name="address2"
              value={profile.address2}
              onChange={handleChange}
              fullWidth
            />
          </Stack>

          {/* Row 5 */}
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              label="City"
              name="city"
              value={profile.city}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="State/Province/Region"
              name="state"
              value={profile.state}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="Zip Code"
              name="zip"
              value={profile.zip}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="Country"
              name="country"
              value={profile.country}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
          </Stack>

          {/* Row 6 */}
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              label="Patient’s Current or Previous Occupation"
              name="occupation"
              value={profile.occupation}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="Marital Status"
              name="maritalStatus"
              placeholder="Married / Single"
              value={profile.maritalStatus}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="Reason for Appointment"
              name="reason"
              value={profile.reason}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
          </Stack>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Emergency Contact Section */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 2, mt: 1 }}
        >
          Emergency Contact Information
        </Typography>

        <Stack spacing={2}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              label="First Name"
              name="emergencyFirst"
              value={profile.emergencyFirst}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="Last Name"
              name="emergencyLast"
              value={profile.emergencyLast}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="Contact Number"
              name="emergencyContact"
              value={profile.emergencyContact}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <TextField
              label="Relation with the patient"
              name="emergencyRelation"
              value={profile.emergencyRelation}
              onChange={handleChange}
              sx={{ flex: 1, minWidth: 250 }}
            />
          </Stack>

          <TextField
            label="Current Healthcare Provider"
            name="healthcareProvider"
            value={profile.healthcareProvider}
            onChange={handleChange}
            fullWidth
          />
        </Stack>

        <Box sx={{ textAlign: "right", mt: 4 }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#007BFF",
              borderRadius: "8px",
              px: 5,
              py: 1.2,
              textTransform: "none",
              fontWeight: 500,
              "&:hover": { backgroundColor: "#0066d6" },
            }}
            onClick={handleSave}
          >
            Save
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};